/**
 * Place tool handler
 * - Shows preview at cursor position (semi-transparent)
 * - Click: create object at world position (snap to grid if enabled)
 * - Creates appropriate default object via factory functions
 * - Adds to stage-store, pushes history
 * - Stays in place mode for rapid placement
 * - Escape: cancel placement, return to select tool
 */

import { Vec2, snapToGrid } from '@/canvas/coordinates';
import { ToolHandler, ToolContext } from './tool-handler';
import { useEditorStore } from '@/core/stores/editor-store';
import { useStageStore } from '@/core/stores/stage-store';
import { useHistoryStore } from '@/core/stores/history-store';
import * as defaults from '@/stage/defaults';
import type { StageObject, Vec3 } from '@/stage/types';

interface PlaceToolState {
  previewPos: Vec2 | null;
}

const state: PlaceToolState = {
  previewPos: null,
};

// Factory map for creating objects by type
type ObjectFactory = (position?: Partial<Vec3>, label?: string) => StageObject;

const objectFactories: Record<string, ObjectFactory> = {
  paper_target: (pos, label) => defaults.createPaperTarget(pos, label),
  steel_target: (pos, label) => defaults.createSteelTarget(pos, label),
  popper: (pos, label) => defaults.createPopper(pos, label),
  plate: (pos, label) => defaults.createPlate(pos, label),
  swinger: (pos, label) => defaults.createSwinger(pos, label),
  drop_turner: (pos, label) => defaults.createDropTurner(pos, label),
  texas_star: (pos, label) => defaults.createTexasStar(pos, label),
  bobber: (pos, label) => defaults.createBobber(pos, label),
  barrel: (pos, label) => defaults.createBarrel(pos, label),
  table: (pos, label) => defaults.createTable(pos, label),
  fault_line: (pos, label) => defaults.createFaultLine(pos, label),
  cone: (pos, label) => defaults.createCone(pos, label),
  no_shoot: (pos, label) => defaults.createNoShoot(pos, label),
  hard_cover: (pos, label) => defaults.createHardCover(pos, label),
  soft_cover: (pos, label) => defaults.createSoftCover(pos, label),
  chair: (pos, label) => defaults.createChair(pos, label),
  door: (pos, label) => defaults.createDoor(pos, label),
  window: (pos, label) => defaults.createWindow(pos, label),
  bed: (pos, label) => defaults.createBed(pos, label),
  couch: (pos, label) => defaults.createCouch(pos, label),
  car: (pos, label) => defaults.createCar(pos, label),
};

export function getPlaceToolHandler(): ToolHandler {
  return {
    onMouseMove(ctx: ToolContext) {
      const { snapToGrid: shouldSnap, gridSize } = useEditorStore.getState();

      state.previewPos = shouldSnap ? snapToGrid(ctx.worldPos, gridSize) : ctx.worldPos;
    },

    onMouseDown(ctx: ToolContext) {
      if (ctx.button !== 0) return; // Only left-click

      const editorState = useEditorStore.getState();
      const stageState = useStageStore.getState();
      const historyState = useHistoryStore.getState();

      const { placingObjectType, gridSize, snapToGrid: shouldSnap } = editorState;
      const { layout } = stageState;

      if (!placingObjectType || !layout) return;

      // Determine position with optional snap
      const pos = shouldSnap ? snapToGrid(ctx.worldPos, gridSize) : ctx.worldPos;

      // Get factory function for this type
      const factory = objectFactories[placingObjectType];
      if (!factory) {
        console.warn(`No factory for object type: ${placingObjectType}`);
        return;
      }

      // Create the object
      const newObject = factory(
        { x: pos.x, y: pos.y, z: 0 },
        `${placingObjectType} ${layout.objects.length + 1}`
      );

      // Push pre-mutation state to history BEFORE mutating
      historyState.pushState(layout);

      // Add to stage
      stageState.addObject(newObject);

      // Mark as dirty
      editorState.setDirty(true);

      // Stay in place mode for rapid placement
    },

    onKeyDown(key: string) {
      if (key === 'Escape') {
        // Cancel placement and return to select tool
        useEditorStore.getState().setTool('select');
      }
    },

    getCursor() {
      return 'crosshair';
    },

    renderPreview(ctx2d: CanvasRenderingContext2D) {
      if (!state.previewPos) return;

      // Draw semi-transparent preview circle/rectangle at cursor
      ctx2d.fillStyle = 'rgba(100, 200, 100, 0.2)';
      ctx2d.strokeStyle = 'rgba(100, 200, 100, 0.8)';
      ctx2d.lineWidth = 0.1;

      // Draw a small circle as preview
      ctx2d.beginPath();
      ctx2d.arc(state.previewPos.x, state.previewPos.y, 0.25, 0, Math.PI * 2);
      ctx2d.fill();
      ctx2d.stroke();
    },
  };
}
